import { useEffect, useState } from 'react';
import { AlertDialog, Button, Callout, Card, Modal, Spinner, Table, TextField } from '@humanforce/mango-ui-react';
import { DetailPair } from './components/SectionShell';
import { WorkerLeaveForecastDialog } from './WorkerLeaveForecastDialog';
import { WorkerLeaveOpeningDialog } from './WorkerLeaveOpeningDialog';
import './WorkerLeavePanel.css';
import {
  endWorkerLeaveRule, exactLeaveHours, getWorkerLeaveAccounts, getWorkerLeaveMovements, getWorkerLeaveRules,
  linkWorkerLeaveRule, removeWorkerLeaveRule, type WorkerLeaveAccount, type WorkerLeaveMovement,
  type WorkerLeavePage, type WorkerLeaveRuleOption,
} from './workerDirectoryApi';

const shown = (value: string | number | null | undefined): string => value === null || value === undefined || value === '' ? '—' : String(value);
const hours = (value: number | null | undefined, exact?: string | null): string => exactLeaveHours(exact, value) ?? '—';
const rate = (value: number | null | undefined): string => value === null || value === undefined ? '—' : value.toFixed(6);

export function WorkerLeavePanel({ personId, canManage, canPostOpening = false, canCorrectOpening = false }: {
  personId: string; canManage: boolean; canPostOpening?: boolean; canCorrectOpening?: boolean;
}) {
  const [accounts, setAccounts] = useState<WorkerLeaveAccount[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picker, setPicker] = useState(false);
  const [search, setSearch] = useState('');
  const [rulePage, setRulePage] = useState(1);
  const [ruleRefreshVersion, setRuleRefreshVersion] = useState(0);
  const [rules, setRules] = useState<WorkerLeavePage<WorkerLeaveRuleOption> | null>(null);
  const [movementPage, setMovementPage] = useState(1);
  const [movementResult, setMovementResult] = useState<{ accountId: string; page: number; data: WorkerLeavePage<WorkerLeaveMovement> | null } | null>(null);
  const [confirmAction, setConfirmAction] = useState<'remove' | 'end' | null>(null);
  const [busy, setBusy] = useState(false);
  const [forecastOpen, setForecastOpen] = useState(false);
  const [openingMode, setOpeningMode] = useState<'open' | 'correct' | null>(null);
  const [movementRefreshVersion, setMovementRefreshVersion] = useState(0);
  const selected = accounts.find((account) => account.id === selectedId) ?? null;
  const movementLoaded = !!selected && !!movementResult && movementResult.accountId === selected.id && movementResult.page === movementPage;
  const movements = movementLoaded ? movementResult.data : null;

  async function reload(preferredId?: string | null) {
    const result = await getWorkerLeaveAccounts(personId);
    setLoading(false);
    if (!result.ok) { setError(result.message ?? 'Could not load Leave accounts.'); return; }
    const next = result.data ?? [];
    setAccounts(next);
    setSelectedId((current) => {
      const wanted = preferredId === undefined ? current : preferredId;
      return next.find((account) => account.id === wanted)?.id ?? next[0]?.id ?? null;
    });
    setError(null);
  }

  useEffect(() => { void reload(); }, [personId]);
  useEffect(() => {
    if (!picker) return;
    let cancelled = false;
    void getWorkerLeaveRules(personId, search, rulePage).then((result) => {
      if (cancelled) return;
      if (result.ok) { setRules(result.data ?? null); setError(null); }
      else setError(result.message ?? 'Could not load Leave Rules.');
    });
    return () => { cancelled = true; };
  }, [picker, personId, search, rulePage, ruleRefreshVersion]);
  useEffect(() => {
    if (!selected) { setMovementResult(null); return; }
    let cancelled = false;
    const accountId = selected.id;
    void getWorkerLeaveMovements(personId, accountId, movementPage).then((result) => {
      if (cancelled) return;
      setMovementResult({ accountId, page: movementPage, data: result.ok ? result.data ?? null : null });
      if (result.ok) setError(null);
      else setError(result.message ?? 'Could not load Leave movements.');
    });
    return () => { cancelled = true; };
  }, [personId, selectedId, movementPage, movementRefreshVersion]);

  async function openingPosted() {
    setMovementResult(null);
    setMovementPage(1);
    setMovementRefreshVersion((version) => version + 1);
    await reload(selectedId);
  }

  async function link(ruleId: string, expectedRuleRowVersion: number) {
    setBusy(true);
    const result = await linkWorkerLeaveRule(personId, ruleId, expectedRuleRowVersion);
    setBusy(false);
    if (!result.ok) {
      await reload(selectedId);
      setError(result.message ?? 'Could not link Leave Rule.');
      if (result.code === 'stale_rule') setRuleRefreshVersion((version) => version + 1);
      return;
    }
    setPicker(false);
    await reload(result.data?.id);
  }

  async function finish() {
    if (!selected || !confirmAction) return;
    setBusy(true);
    const result = confirmAction === 'remove'
      ? await removeWorkerLeaveRule(personId, selected.personLeaveRuleAssignmentId, selected.rowVersion)
      : await endWorkerLeaveRule(personId, selected.personLeaveRuleAssignmentId, selected.rowVersion);
    setBusy(false);
    setConfirmAction(null);
    await reload(result.ok && confirmAction === 'remove' ? null : selected.id);
    if (!result.ok) setError(result.message ?? 'Could not change the Leave link.');
  }

  const fields: Array<[string, string | number | null | undefined]> = selected ? [
    ['Leave Type', selected.leaveTypeName], ['Leave Type code', selected.leaveTypeCode],
    ['Leave Rule', selected.ruleName], ['Rule code', selected.ruleCode], ['Rule version', selected.ruleVersion],
    ['Rule source', selected.ruleSource], ['Assignment status', selected.assignmentStatus],
    ['Assignment effective from', selected.assignmentEffectiveFrom], ['Assignment effective to', selected.assignmentEffectiveTo],
    ['Requested by', selected.requestedByUserId], ['Requested at', selected.requestedAt],
    ['Approved by', selected.approvedByUserId], ['Approved at', selected.approvedAt],
    ['Work Assignment', selected.workAssignmentId],
    ['Balance (hours)', hours(selected.currentBalanceHours, selected.currentBalanceHoursExact)], ['Accrual rate', rate(selected.accrualRate)],
    ['Accrual basis', selected.accrualBasis], ['Jurisdictional state', selected.jurisdictionalState],
    ['Accrual start date', selected.accrualStartDate], ['Entitlement date', selected.entitlementDate],
    ['Entitlement', hours(selected.entitlement)], ['Prorate', selected.prorate],
    ['Last accrual posted at', selected.lastAccrualPostedAt],
    ['Held average ordinary hours per week', hours(selected.heldAvgOrdinaryHoursPerWeek)],
    ['Held average ordinary rate per week', hours(selected.heldAvgOrdinaryRatePerWeek)],
    ['Held average as at pay end date', selected.heldAvgAsAtPayEndDate],
    ['Account effective start', selected.effectiveStartDate], ['Account effective end', selected.effectiveEndDate],
    ['Account status', selected.accountStatus], ['Closed at', selected.closedAt],
    ['Created at', selected.createdAt], ['Created by', selected.createdBy],
    ['Updated at', selected.updatedAt], ['Updated by', selected.updatedBy],
  ] : [];

  return <div className="mgo-stack mgo-gap-l" data-testid="worker-leave-panel">
    {error && <Callout tone="critical" title="Leave request failed">{error}</Callout>}
    {loading ? <div className="mgo-cluster mgo-gap-s"><Spinner /> Loading Leave…</div> : <>
      <div className="mgo-cluster mgo-justify-end">
        {canManage && <Button label="Link Leave Rule" variant="primary" onClick={() => { setRulePage(1); setPicker(true); }} />}
      </div>
      <div className="worker-leave-layout" data-testid="leave-split">
        <div data-testid="leave-grid">
          <Card title="Leave accounts">
            <div className="worker-leave-table-scroll"><Table value={accounts} emptyMessage="No Leave Rules linked" testId="leave-accounts-table"
              onRowClick={(row: WorkerLeaveAccount) => { setSelectedId(row.id); setMovementPage(1); }}
              columns={[
                { field: 'leaveTypeName', header: 'Leave Type', body: (row: WorkerLeaveAccount) =>
                  <button type="button" className="worker-leave-select mgo-interactive mgo-padding-s"
                    aria-label={`Select ${row.leaveTypeName}`} aria-pressed={selectedId === row.id}
                    onClick={() => { setSelectedId(row.id); setMovementPage(1); }}>{row.leaveTypeName}</button> },
                { field: 'ruleName', header: 'Linked Rule' },
                { field: 'currentBalanceHours', header: 'Balance (hours)', body: (row: WorkerLeaveAccount) => hours(row.currentBalanceHours, row.currentBalanceHoursExact) },
                { field: 'accountStatus', header: 'Status' },
              ]} /></div>
          </Card>
        </div>
        <div data-testid="leave-details">
          <Card title={selected ? `${selected.leaveTypeName} details` : 'Selected Leave Rule'}>
            {selected ? <div className="mgo-stack mgo-gap-l">
              <Button label="Forecast Leave" variant="secondary" fullWidth onClick={() => setForecastOpen(true)} />
              {canPostOpening && selected.opening?.canOpen &&
                <Button label="Enter Opening Balance" variant="secondary" fullWidth onClick={() => setOpeningMode('open')} />}
              {canCorrectOpening && selected.opening?.canCorrect &&
                <Button label="Correct Opening Balance" variant="secondary" fullWidth onClick={() => setOpeningMode('correct')} />}
              {selected.opening?.currentMovementId && <div className="mgo-stack mgo-gap-s" aria-label="Current opening audit">
                <strong>Current opening</strong>
                <DetailPair label="Opening amount (hours)" value={hours(selected.opening.currentOpeningHours, selected.opening.currentOpeningHoursExact)} />
                <DetailPair label="Balance as-of date" value={shown(selected.opening.balanceAsOfDate)} />
                <DetailPair label="Historical covered through" value={shown(selected.opening.accrualCoveredThroughDate)} />
                <DetailPair label="Reason" value={shown(selected.opening.reason)} />
                <DetailPair label="Source reference" value={shown(selected.opening.sourceReference)} />
                <DetailPair label="Posted at" value={shown(selected.opening.postedAt)} />
                <DetailPair label="Posted by" value={shown(selected.opening.postedBy)} />
                <DetailPair label="Movement reference" value={selected.opening.currentMovementId} />
              </div>}
              {canPostOpening && selected.opening?.ineligibleReason && !selected.opening.canCorrect &&
                <Callout tone="caution" title="Opening unavailable">{selected.opening.ineligibleReason}</Callout>}
              <div className="mgo-stack mgo-gap-s">{fields.map(([field, value]) => <DetailPair key={field} label={field} value={shown(value)} />)}</div>
              <div className="mgo-stack mgo-gap-s" id="worker-leave-movements" tabIndex={-1}>
                <div className="mgo-cluster mgo-gap-s"><strong>Movement history</strong>
                  <Button label="View Movements" variant="tertiary" onClick={() => document.getElementById('worker-leave-movements')?.focus()} />
                </div>
                {movements?.items.length ? <>
                  <div className="worker-leave-table-scroll"><Table value={movements.items} emptyMessage="No movements"
                    columns={[
                      { field: 'effectiveDate', header: 'Effective date' },
                      { field: 'movementType', header: 'Type' },
                      { field: 'quantityHours', header: 'Hours', body: (row: WorkerLeaveMovement) => hours(row.quantityHours, row.quantityHoursExact) },
                      { field: 'sourceSystem', header: 'Source system' },
                      { field: 'sourceReference', header: 'Source reference' },
                      { field: 'sourceRevision', header: 'Source revision', body: (row: WorkerLeaveMovement) => shown(row.sourceRevision) },
                      { field: 'sourceSnapshotDigest', header: 'Snapshot digest', body: (row: WorkerLeaveMovement) => shown(row.sourceSnapshotDigest) },
                      { field: 'payRunId', header: 'Pay Run', body: (row: WorkerLeaveMovement) => shown(row.payRunId) },
                      { field: 'payRunBatchId', header: 'Pay Run batch', body: (row: WorkerLeaveMovement) => shown(row.payRunBatchId) },
                      { field: 'payRunTransactionId', header: 'Pay Run transaction', body: (row: WorkerLeaveMovement) => shown(row.payRunTransactionId) },
                      { field: 'idempotencyKey', header: 'Idempotency key' },
                      { field: 'reversalOfMovementId', header: 'Reversal of', body: (row: WorkerLeaveMovement) => shown(row.reversalOfMovementId) },
                      { field: 'correctionOfMovementId', header: 'Correction of', body: (row: WorkerLeaveMovement) => shown(row.correctionOfMovementId) },
                      { field: 'reason', header: 'Reason', body: (row: WorkerLeaveMovement) => shown(row.reason) },
                      { field: 'balanceAsOfDate', header: 'Balance as-of', body: (row: WorkerLeaveMovement) => shown(row.balanceAsOfDate) },
                      { field: 'accrualCoveredThroughDate', header: 'Historical covered through', body: (row: WorkerLeaveMovement) => shown(row.accrualCoveredThroughDate) },
                      { field: 'operationReference', header: 'Operation reference', body: (row: WorkerLeaveMovement) => shown(row.operationReference) },
                      { field: 'createdBy', header: 'Posted by' },
                      { field: 'balanceAfterHours', header: 'Balance after', body: (row: WorkerLeaveMovement) => hours(row.balanceAfterHours, row.balanceAfterHoursExact) },
                      { field: 'postedAt', header: 'Posted' },
                    ]} /></div>
                  <div className="mgo-cluster mgo-gap-s">
                    <Button label="Previous movements" variant="tertiary" disabled={movementPage <= 1} onClick={() => setMovementPage(movementPage - 1)} />
                    <Button label="Next movements" variant="tertiary" disabled={movementPage >= movements.totalPages} onClick={() => setMovementPage(movementPage + 1)} />
                  </div>
                </> : movementLoaded ? <p>{movements ? 'No movements have been posted.' : 'Movement history unavailable.'}</p> : <div className="mgo-cluster mgo-gap-s"><Spinner /> Loading movements…</div>}
              </div>
              {canManage && selected.canRemove && <Button label="Remove unused link" variant="destructive" onClick={() => setConfirmAction('remove')} />}
              {canManage && selected.canEnd && <Button label="End historical link" variant="secondary" onClick={() => setConfirmAction('end')} />}
              {selected.removalBlockedReason && <Callout tone="caution" title="Link cannot be removed">{selected.removalBlockedReason}</Callout>}
            </div> : <p>Select a Leave account to view details and movements.</p>}
          </Card>
        </div>
      </div>
    </>}
    <Modal visible={picker} header="Link Leave Rule" size="large" onHide={() => setPicker(false)}>
      <div className="mgo-stack mgo-gap-l">
        <TextField label="Search Leave Rules" type="search" value={search} onChange={(event) => { setSearch(event.target.value); setRulePage(1); }} />
        <div className="worker-leave-table-scroll"><Table value={rules?.items ?? []} emptyMessage="No Leave Rules available" columns={[
          { field: 'ineligibleReason', header: 'Action', body: (row: WorkerLeaveRuleOption) =>
            row.eligible ? <Button label={`Link ${row.ruleName}`} variant="secondary" disabled={busy} onClick={() => void link(row.id, row.rowVersion)} />
              : <span>{row.ineligibleReason}</span> },
          { field: 'leaveTypeName', header: 'Leave Type' }, { field: 'ruleName', header: 'Rule' },
          { field: 'versionNumber', header: 'Version' }, { field: 'jurisdictionCode', header: 'Jurisdiction' },
          { field: 'effectiveFrom', header: 'From' }, { field: 'effectiveTo', header: 'To' },
        ]} /></div>
        <div className="mgo-cluster mgo-gap-s">
          <Button label="Previous rules" variant="tertiary" disabled={rulePage <= 1} onClick={() => setRulePage(rulePage - 1)} />
          <Button label="Next rules" variant="tertiary" disabled={!rules || rulePage >= rules.totalPages} onClick={() => setRulePage(rulePage + 1)} />
        </div>
      </div>
    </Modal>
    <WorkerLeaveForecastDialog key={`${personId}:${selectedId ?? ''}`} personId={personId} account={selected}
      visible={forecastOpen && selected !== null} onHide={() => setForecastOpen(false)} />
    {openingMode !== null && <WorkerLeaveOpeningDialog key={`${personId}:${selectedId ?? ''}`} personId={personId} account={selected}
      mode={openingMode} onHide={() => setOpeningMode(null)} onPosted={openingPosted} />}
    <AlertDialog visible={confirmAction !== null} header={confirmAction === 'end' ? 'End Leave link?' : 'Remove unused Leave link?'}
      severity="warning" confirmLabel={confirmAction === 'end' ? 'End link' : 'Remove link'} confirmLoading={busy}
      onConfirm={() => void finish()} onCancel={() => setConfirmAction(null)}>
      {confirmAction === 'end' ? 'The account and its movement history will be retained.' : 'The unused zero-balance account and link will be removed.'}
    </AlertDialog>
  </div>;
}
